/**
 * Annotation property getters and registration - NON-HOOK VERSION FOR POTREE
 *
 * This file contains only framework-agnostic code (no React hooks, no Apollo).
 * It can be safely imported by potree_fork without pulling in React dependencies.
 *
 * Registers properties shared by ALL annotation types:
 * - annotation_color (COLOR hex string, PROJECT_GLOBAL)
 * - annotation_visible (BOOLEAN, PROJECT_USER)
 *
 * For React hook versions, see AnnotationProperties.ts
 */

import { AnnotationType, PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService, hasGlobalPropertyService } from '../../reactive/globalPropertyService';

/** Annotation types that support object color (wireframe/outline) */
const COLOR_ANNOTATION_TYPES = [AnnotationType.POINT, AnnotationType.DISTANCE, AnnotationType.AREA, AnnotationType.BOX, AnnotationType.POINTCLUSTER, AnnotationType.ROOM, AnnotationType.WALL_SKETCH, AnnotationType.OPENING, AnnotationType.OUTLINE];

// ============================================================================
// PROPERTY REGISTRATION
// ============================================================================

/**
 * Register annotation properties in the registry.
 * Call this once at app initialization.
 */
export function registerAnnotationProperties() {
  // annotation_name: registered with annotationTypes: [] so PropertyPanel excludes it.
  // Name is edited via inline UI, not the property panel.
  if (!propertyRegistry.isRegistered('annotation_name')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'annotation_name',
      name: 'Name',
      valueType: PropertyValueType.STRING,
      defaultValue: '',
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('annotation_color')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'annotation_color',
      name: 'Object color',
      valueType: PropertyValueType.COLOR,
      defaultValue: '#9600fa',
      getDefaultValue: () => {
        try {
          if (hasGlobalPropertyService()) {
            const value = getGlobalPropertyService().getProperty('user_default_object_color', '', PropertyType.USER_SETTINGS).value;
            if (typeof value === 'string' && value.startsWith('#')) return value;
          }
        } catch {
          /* service not ready */
        }
        return '#9600fa';
      },
      ui: {
        group: 'appearance',
        order: 4,
      },
      annotationTypes: COLOR_ANNOTATION_TYPES,
    });
  }

  if (!propertyRegistry.isRegistered('annotation_visible')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      identifier: 'annotation_visible',
      name: 'Visible',
      valueType: PropertyValueType.BOOLEAN,
      defaultValue: true,
      annotationTypes: [],
    });
  }

  // annotation_class: classification code for PointCluster annotations.
  if (!propertyRegistry.isRegistered('annotation_class')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'annotation_class',
      name: 'Classification',
      valueType: PropertyValueType.INT,
      defaultValue: 0,
      annotationTypes: [AnnotationType.POINTCLUSTER],
    });
  }

  // fill_color: solid fill color for Box, Plane, and Surface annotations.
  // annotation_color is used for the wireframe/outline (consistent across all types).
  if (!propertyRegistry.isRegistered('fill_color')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'fill_color',
      name: 'Fill Color',
      valueType: PropertyValueType.COLOR,
      defaultValue: '#2f5954',
      description: 'Solid fill color for the annotation',
      ui: {
        tooltip: 'Select the fill color for the annotation body',
        group: 'appearance',
        order: 5,
      },
      annotationTypes: [AnnotationType.BOX],
    });
  }
}

// Register properties immediately when module is imported
registerAnnotationProperties();

// ============================================================================
// NON-HOOK HELPERS FOR POTREE (uses global property service)
// ============================================================================

/** Get reactive property for annotation name (not registered in PropertyPanel — managed via inline UI) */
export function getAnnotationNameProperty(annotationId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('annotation_name', annotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

/** Get reactive property for annotation color (hex string, e.g. '#9600fa') */
export function getAnnotationColorProperty(annotationId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('annotation_color', annotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

/** Get reactive property for annotation visibility */
export function getAnnotationVisibleProperty(annotationId: string): ReactiveProperty<boolean> {
  const service = getGlobalPropertyService();
  return service.getProperty('annotation_visible', annotationId, PropertyType.PROJECT_USER) as ReactiveProperty<boolean>;
}

/** Get reactive property for annotation class (PointCluster classification code) */
export function getAnnotationClassProperty(annotationId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('annotation_class', annotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}

/** Get reactive property for fill color (Box, Plane, Surface annotations) */
export function getFillColorProperty(annotationId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('fill_color', annotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

/** @deprecated Use getFillColorProperty instead */
export function getFrameColorProperty(annotationId: string): ReactiveProperty<string> {
  return getFillColorProperty(annotationId);
}
